import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../utils/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import SinceYouStarted, { readSentence } from './SinceYouStarted';

// Your stack (2026-10 restyle). One card per supplement: name and dose, how
// long you've been on it, one sentence about what moved, the list of what we
// checked. Change the dose or date, or say you stopped, from the card.
// Medications are kept only to warn about clashes; we never advise on them.

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '');
const sinceText = (iso) => {
  if (!iso) return 'No start date yet';
  const days = Math.max(0, Math.round((new Date() - new Date(`${iso}T00:00:00`)) / 86400000));
  const span = days < 14 ? `${days} day${days === 1 ? '' : 's'}` : days < 100 ? `${Math.round(days / 7)} weeks` : `${Math.round(days / 30)} months`;
  return `Since ${fmtDate(iso)} · ${span}`;
};
const doseText = (it) => [it.dose, it.dose_unit].filter((x) => x !== null && x !== '' && x !== undefined).join(' ');

const Quiet = ({ children, ...props }) => (
  <button type="button" className="bg-transparent p-0 text-sm font-medium text-fg-2 hover:text-fg disabled:opacity-50" {...props}>{children}</button>
);

// Change dose or date, or stop, inline under the card.
const EditRow = ({ item, onDone, onError }) => {
  const [dose, setDose] = useState(item.dose ?? '');
  const [unit, setUnit] = useState(item.dose_unit || '');
  const [started, setStarted] = useState(item.started_at || todayIso());
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/api/regimen/${item.id}`, {
        dose: dose === '' ? null : Number(dose), dose_unit: unit || null, started_at: started || null,
      });
      onDone();
    } catch { onError('Could not save that.'); } finally { setBusy(false); }
  };
  return (
    <div className="col-span-full mt-2 flex flex-wrap items-end gap-3 border-t border-line pt-3.5">
      <label className="text-sm text-fg-3">Dose<Input className="mt-1 w-24" inputMode="decimal" value={dose} onChange={(e) => setDose(e.target.value)} /></label>
      <label className="text-sm text-fg-3">Unit<Input className="mt-1 w-24" value={unit} onChange={(e) => setUnit(e.target.value)} /></label>
      <label className="text-sm text-fg-3">Started<Input type="date" className="mt-1 w-auto" value={started} max={todayIso()} onChange={(e) => setStarted(e.target.value)} /></label>
      <Button size="sm" onClick={save} disabled={busy}>Save</Button>
      <Quiet onClick={onDone}>Cancel</Quiet>
    </div>
  );
};

const StackManager = () => {
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [reads, setReads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [medOpen, setMedOpen] = useState(false);

  const [suppSlug, setSuppSlug] = useState('');
  const [suppName, setSuppName] = useState('');
  const [suppDose, setSuppDose] = useState('');
  const [suppUnit, setSuppUnit] = useState('');
  const [suppStart, setSuppStart] = useState(todayIso());
  const [medName, setMedName] = useState('');
  const [medDose, setMedDose] = useState('');
  const [medUnit, setMedUnit] = useState('');

  const catalogBySlug = useMemo(() => Object.fromEntries(catalog.map((c) => [c.slug, c])), [catalog]);

  const load = async () => {
    try {
      const [cat, reg] = await Promise.all([apiClient.get('/api/supplements/catalog'), apiClient.get('/api/regimen')]);
      setCatalog(cat.data || []);
      setItems(reg.data || []);
    } catch { setError('Could not load your stack.'); } finally { setLoading(false); }
    try {
      const res = await apiClient.get('/api/regimen/reads');
      setReads(res.data?.items || []);
    } catch { setReads([]); }
  };
  useEffect(() => { load(); }, []);

  const supplements = items.filter((i) => i.kind === 'supplement');
  const medications = items.filter((i) => i.kind === 'medication');
  const readById = useMemo(() => Object.fromEntries(reads.map((r) => [r.item_id, r])), [reads]);

  const pickCatalog = (slug) => {
    setSuppSlug(slug);
    const c = catalogBySlug[slug];
    if (c) { setSuppName(c.name); setSuppDose(c.default_dose ?? ''); setSuppUnit(c.default_dose_unit || ''); }
  };

  const addSupplement = async () => {
    const name = (suppName || catalogBySlug[suppSlug]?.name || '').trim();
    if (!name) { setError('Pick a supplement or type a name.'); return; }
    setBusy(true); setError('');
    try {
      await apiClient.post('/api/regimen', {
        kind: 'supplement', catalog_slug: suppSlug || null, name,
        dose: suppDose === '' ? null : Number(suppDose), dose_unit: suppUnit || null, started_at: suppStart || null,
      });
      setSuppSlug(''); setSuppName(''); setSuppDose(''); setSuppUnit(''); setSuppStart(todayIso()); setAdding(false);
      load();
    } catch { setError('Could not add that.'); } finally { setBusy(false); }
  };

  const addMedication = async () => {
    if (!medName.trim()) { setError('Type the medication’s name.'); return; }
    setBusy(true); setError('');
    try {
      await apiClient.post('/api/regimen', { kind: 'medication', name: medName.trim(), dose: medDose === '' ? null : Number(medDose), dose_unit: medUnit || null, prescribed: true });
      setMedName(''); setMedDose(''); setMedUnit(''); setMedOpen(false);
      load();
    } catch { setError('Could not add that.'); } finally { setBusy(false); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Take ${it.name} off your stack?`)) return;
    try { await apiClient.delete(`/api/regimen/${it.id}`); load(); } catch { setError('Could not remove that.'); }
  };

  const AddForm = (
    <div className="rounded-xl border border-line px-5 py-4">
      <p className="text-base font-medium">Add a supplement</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="min-w-[12rem] flex-1 text-sm text-fg-3">From the list
          <select value={suppSlug} onChange={(e) => pickCatalog(e.target.value)}
            className="dark-select mt-1 flex h-10 w-full rounded-lg border border-line-strong bg-elev px-3 text-base text-fg focus-visible:outline-none">
            <option value="">Pick one…</option>
            {catalog.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="min-w-[10rem] flex-1 text-sm text-fg-3">Or type your own
          <Input className="mt-1" value={suppSlug ? '' : suppName} onChange={(e) => { setSuppSlug(''); setSuppName(e.target.value); }} />
        </label>
        <label className="text-sm text-fg-3">Dose<Input className="mt-1 w-24" inputMode="decimal" value={suppDose} onChange={(e) => setSuppDose(e.target.value)} /></label>
        <label className="text-sm text-fg-3">Unit<Input className="mt-1 w-24" value={suppUnit} onChange={(e) => setSuppUnit(e.target.value)} /></label>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-sm text-fg-3">When did you start?<Input type="date" className="mt-1 w-auto" value={suppStart} max={todayIso()} onChange={(e) => setSuppStart(e.target.value)} /></label>
        <Button onClick={addSupplement} disabled={busy}>Add</Button>
        <Quiet onClick={() => setAdding(false)}>Cancel</Quiet>
      </div>
      {suppSlug && catalogBySlug[suppSlug]?.food_first_note && (
        <p className="mt-3 text-sm text-fg-3">Food first: {catalogBySlug[suppSlug].food_first_note}</p>
      )}
    </div>
  );

  if (loading) return <div className="mt-10 h-32 animate-pulse rounded-xl border border-line" />;

  return (
    <>
      {error && <p className="mt-4 text-sm text-off-strong dark:text-off">{error}</p>}

      <section className="mt-10" id="stack">
        <h2 className="mb-3.5 text-[1.2rem] font-medium tracking-[-0.015em]">Your stack</h2>
        {supplements.length === 0 && !adding && (
          <p className="mb-3.5 text-fg-3">Nothing here yet. Add what you take and tell us when you started.</p>
        )}
        <div className="grid gap-3.5">
          {supplements.map((it) => {
            const read = readById[it.id];
            const c = it.catalog_slug ? catalogBySlug[it.catalog_slug] : null;
            return (
              <article key={it.id} className="grid gap-x-7 gap-y-2 rounded-2xl border border-line bg-raised px-5 pb-4 pt-[18px]">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="text-[1.25rem] font-medium tracking-[-0.015em]">
                    {it.name}{doseText(it) && <small className="ml-2 text-base font-normal text-fg-3">{doseText(it)}</small>}
                  </div>
                  <div className="text-sm text-fg-3">{sinceText(it.started_at)}</div>
                </div>

                {it.started_at ? (
                  read ? (
                    <>
                      <p className="text-[1.15rem] font-medium leading-[1.3] tracking-[-0.01em]">{readSentence(read)}</p>
                      <SinceYouStarted read={read} />
                    </>
                  ) : (
                    <p className="text-fg-3">{c?.structure_function_claim || 'We’ll read what moved once your data covers the start.'}</p>
                  )
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-fg-2">When did you start it? Then we can read what moved.</p>
                    <Quiet onClick={() => setEditing(it.id)}>Set the date →</Quiet>
                  </div>
                )}

                {c?.food_first_note && it.started_at && !read && (
                  <p className="text-sm text-fg-3">Food first: {c.food_first_note}</p>
                )}

                <div className="mt-1 flex flex-wrap items-center gap-4 border-t border-line pt-2.5 text-sm text-fg-3">
                  {read && read.outcomes?.some((r) => r.verdict === 'moved') && <span>Moved alongside it. We can’t say it caused it.</span>}
                  <Quiet onClick={() => setEditing(editing === it.id ? null : it.id)}>Change dose or date</Quiet>
                  <Quiet onClick={() => removeItem(it)}>Stopped taking it</Quiet>
                </div>
                {editing === it.id && <EditRow item={it} onDone={() => { setEditing(null); load(); }} onError={setError} />}
              </article>
            );
          })}
        </div>

        <div className="mt-3.5" id="add">
          {adding ? AddForm : (
            <button type="button" onClick={() => setAdding(true)}
              className="grid min-h-[96px] w-full place-items-center rounded-xl border border-dashed border-line-strong bg-transparent px-5 py-5 text-center text-[1.05rem] font-medium text-fg-2 transition-colors hover:bg-raised hover:text-fg">
              <span>+ Add a supplement<small className="mt-1.5 block text-sm font-normal text-fg-3">Pick from the list or type your own. Tell us when you started.</small></span>
            </button>
          )}
        </div>
      </section>

      <section className="mt-10">
        <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">Medications</h2>
            <p className="mt-1 text-sm text-fg-3">We only use these to warn you about clashes. We never advise on a dose.</p>
          </div>
          {!medOpen && <Quiet onClick={() => { setMedOpen(true); setError(''); }}>+ Add one</Quiet>}
        </div>
        <div className="grid gap-3">
          {medications.map((it) => (
            <div key={it.id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line px-5 py-4">
              <div className="text-[1.1rem] font-medium">{it.name}{doseText(it) && <span className="ml-2 font-normal text-fg-3">{doseText(it)}</span>}</div>
              <Quiet onClick={() => removeItem(it)}>Remove</Quiet>
            </div>
          ))}
          {medications.length === 0 && !medOpen && <p className="text-fg-3">None on file.</p>}
          {medOpen && (
            <div className="rounded-xl border border-line px-5 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <label className="min-w-[12rem] flex-1 text-sm text-fg-3">Name<Input className="mt-1" value={medName} onChange={(e) => setMedName(e.target.value)} /></label>
                <label className="text-sm text-fg-3">Dose<Input className="mt-1 w-24" inputMode="decimal" value={medDose} onChange={(e) => setMedDose(e.target.value)} /></label>
                <label className="text-sm text-fg-3">Unit<Input className="mt-1 w-24" value={medUnit} onChange={(e) => setMedUnit(e.target.value)} /></label>
                <Button onClick={addMedication} disabled={busy}>Add</Button>
                <Quiet onClick={() => setMedOpen(false)}>Cancel</Quiet>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default StackManager;
